import {
    AddPatientRequest,
    CreatorPromptField,
    DocumentAsset,
    DocumentType,
    EzyvetSectionMapEntry,
    ImportFilePayload,
    ImportFileResponse,
    PatientRecord,
    PIMSType,
    ReleaseNotes,
    ShepherdTemplateFieldMapEntry,
    TranscriptSectionStatus,
    ValidateType,
} from "../utils/types";
import { Session } from "@supabase/supabase-js";
import { VITE_HAPPYDOC_TRANSCRIBE_SERVER_URL, VITE_HAPPYDOC_API_SERVER_URL } from "@common/utils/constants";
import * as Sentry from "@sentry/react";
import { DateTime } from "luxon";
import ky, { Options as KyOptions } from "ky";
import { getStoredSelectedClinicAccountId } from "@common/lib/selectedClinicAccountStorage";
import { retryWrapper } from "@common/lib/retryWrapper";
import { classifyNetworkError, notifyNetworkError } from "@common/lib/sentryUtils";
import { getClientInterruption } from "@common/lib/requestInterruption";
import type { ClientRecordingMetadata } from "@common/lib/recordingTelemetry";
import { MONITORING_DOCUMENT_TYPE_ID } from "@common/utils/monitoring";

// Helper function to get effective session for impersonation
export const getEffectiveSession = async () => {
    try {
        // Import ImpersonationService dynamically to avoid circular dependencies
        const { ImpersonationService } = await import("./impersonationService");
        return await ImpersonationService.getEffectiveSession();
    } catch (error) {
        console.error("Error getting effective session:", error);
        return null;
    }
};

const DEFAULT_API_TIMEOUT_MS = 30000;

export const api = ky.create({
    prefixUrl: VITE_HAPPYDOC_API_SERVER_URL,
    timeout: DEFAULT_API_TIMEOUT_MS,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

export type ApiFetchOptions = KyOptions & {
    skipAuth?: boolean;
    skipSentryOn404?: boolean;
    // Response statuses that are an expected, benign outcome for this endpoint and
    // must NOT be reported to Sentry (e.g. a 401 from group/usage/me simply means
    // "not a corporate-group user" for the vast majority of logins).
    skipSentryOnStatuses?: number[];
    // EN-1253 Ruling 20: the caller reports failures itself (e.g. a background poll that logs
    // once per failure streak). Skips both the Sentry capture and the network-error toast; the
    // error is still thrown.
    silent?: boolean;
};

/**
 * Authenticated request to the HappyDoc API. Uses effective session (impersonation-aware).
 * Set `skipAuth: true` for endpoints that must work without a session (rare; e.g. device unlock polling).
 * Set `skipSentryOn404: true` for endpoints where 404 is an expected, benign outcome (e.g. optional prompts).
 * Set `skipSentryOnStatuses: [...]` for endpoints where specific statuses are expected (e.g. [401]).
 * Set `silent: true` when the caller reports failures itself: no Sentry capture, no toast, still throws.
 * For 204 No Content / 205 Reset Content, returns undefined; callers expecting a JSON body
 * must handle undefined or use a type that includes it (e.g. Promise<MyType | undefined>).
 */
export const apiFetch = async <T>(
    path: string,
    options: ApiFetchOptions = { method: "GET" },
): Promise<T | undefined> => {
    const startedAt = Date.now();
    try {
        const { skipAuth, skipSentryOn404, skipSentryOnStatuses, silent, ...kyOptions } = options;

        let authHeaders: Record<string, string> = {};
        if (!skipAuth) {
            const effectiveSession = await getEffectiveSession();

            if (!effectiveSession) {
                throw new Error("No effective session found");
            }

            const { userId, accessToken, isImpersonating } = effectiveSession;
            const selectedClinicAccountId = getStoredSelectedClinicAccountId(userId);
            authHeaders = {
                Authorization: `Bearer ${accessToken}`,
                ...(isImpersonating ? { "X-Effective-User-Id": userId } : {}),
                ...(selectedClinicAccountId ? { "X-HappyDoc-Account-Id": selectedClinicAccountId } : {}),
            };
        }

        const response = await api(path, {
            ...kyOptions,
            headers: {
                ...kyOptions.headers,
                ...authHeaders,
            },
        });

        // 204 No Content / 205 Reset Content have no body; .json() would throw
        if (response.status === 204 || response.status === 205) {
            return undefined;
        }
        return await response.json();
    } catch (error) {
        // Silently drop aborted requests — React unmounting components mid-flight is expected
        // behaviour, not an error. Chrome reports these as "TypeError: Failed to fetch" rather
        // than a proper AbortError, so we check both the error name and the signal state.
        const isAbort =
            (error as any)?.name === "AbortError" ||
            (error as any)?.message?.toLowerCase().includes("aborted") ||
            options.signal?.aborted;
        if (isAbort) {
            throw error;
        }

        const status = (error as any)?.response?.status;
        const is404 = status === 404;
        const isExpectedStatus =
            (is404 && options.skipSentryOn404) ||
            (typeof status === "number" && (options.skipSentryOnStatuses?.includes(status) ?? false));
        if (!isExpectedStatus && !options.silent) {
            const errorCategory = classifyNetworkError(error);
            // EN-1224: a timeout or network failure while the machine slept, the tab was frozen,
            // or the browser was offline usually never reached the server. Report it as its own
            // warning-level issue so backend_latency only counts real server slowness, and skip
            // the "server is slow" toast for a request the user wasn't waiting on. Only failures with
            // no HTTP response qualify: a status means some server got the request and answered it,
            // which is worth surfacing as-is even if the client slept around it.
            const interruption =
                errorCategory === "code_bug" || status !== undefined
                    ? null
                    : getClientInterruption(startedAt, options.timeout ?? DEFAULT_API_TIMEOUT_MS);
            Sentry.captureException(error, {
                ...(interruption ? { level: "warning", fingerprint: ["{{ default }}", "client-interrupted"] } : {}),
                tags: {
                    errorType: "api_fetch_error",
                    errorCategory: interruption ? "client_interrupted" : errorCategory,
                    ...(interruption ? { clientInterruption: interruption } : {}),
                    endpoint: path,
                },
                extra: {
                    apiServerUrl: VITE_HAPPYDOC_API_SERVER_URL,
                    method: options.method ?? "GET",
                    userAgent: navigator.userAgent,
                    online: navigator.onLine,
                    connectionType: (navigator as any).connection?.effectiveType ?? "unknown",
                    timestamp: new Date().toISOString(),
                },
            });
            if (!interruption) {
                notifyNetworkError(errorCategory);
            }
        }
        throw error;
    }
};

/** Throws if value is undefined (e.g. 204/205 response when a JSON body was expected). */
export function expectJsonBody<T>(value: T | undefined, endpoint?: string): T {
    if (value === undefined) {
        throw new Error(endpoint ? `Expected response body from ${endpoint}` : "Expected response body");
    }
    return value;
}

export const transcribeAudio = async (
    transcriptionId: string,
    transcriptSectionId: string,
    audioName: string,
    patientRecordId?: string | null,
    patientName: string = "",
    doctorId: string = "",
    documentIds: string[] = [],
    documentAssetIds: string[] = [],
    transcriptSectionIds: string[] = [],
    clientMetadata?: ClientRecordingMetadata | null,
    targetChatCompletionId?: string | null,
    // EN-1266: the first folder of the storage path this audio was uploaded to (the clinic
    // account id for web uploads since PR #2161). Transcribe builds its download path from
    // `userId`, so it must match the upload path, not the viewer's own login.
    storageAccountId?: string | null,
): Promise<{ error: null; data: any } | { error: any; data: null }> => {
    const effectiveSession = await getEffectiveSession();

    if (!effectiveSession) {
        Sentry.captureException(new Error("No effective session found"));
        return { error: new Error("No effective session found"), data: null };
    }

    const { session, userId, accessToken } = effectiveSession;

    // Monitoring is generated exclusively through its own dedicated endpoints (append/batch/
    // start-day), triggered either by the live-recording targetChatCompletionId mechanism below
    // or by GenerateButton's explicit monitoringSelected/standardDocIds split — never by this
    // generic "generate these document types after transcription" path, which every caller of
    // transcribeAudio (generate-on-stop, retry-transcription, bulk upload/retry) shares. Filtering
    // it out here, at the one place all of those callers converge, protects all of them at once
    // instead of requiring each call site to remember to exclude it individually.
    const filteredDocumentIds = documentIds.filter((id) => id !== MONITORING_DOCUMENT_TYPE_ID);

    // EN-1266: with no storage account, Transcribe is sent the viewer's own id and has to find the
    // clinic folder through its fallback. Legitimate for legacy rows, but leave a trail so a caller
    // that lost the account is visible next to any later failure.
    if (!storageAccountId) {
        Sentry.addBreadcrumb({
            category: "transcribe",
            message: "transcribeAudio called without storageAccountId; using the viewer's id",
            data: { transcriptionId, transcriptSectionId },
            level: "info",
        });
    }

    const postData = {
        userId: storageAccountId || userId,
        // Hint for Transcribe's fallback when older audio lives under the uploader's own folder.
        uploaderUserId: userId,
        transcriptionId,
        transcriptSectionId,
        audioName,
        realtimeClientId: session.user.id,
        patientRecordId,
        patientName,
        doctorId,
        documentIds: filteredDocumentIds,
        documentAssetIds,
        transcriptSectionIds,
        // Catch-all client observability blob. Transcribe writes it to
        // transcript_sections.client_metadata. Shape lives in
        // recordingTelemetry.ts; Transcribe treats this as opaque pass-through
        // so adding new fields here doesn't require a backend change.
        clientMetadata: clientMetadata ?? null,
        targetChatCompletionId: targetChatCompletionId ?? null,
    };

    const url = `${VITE_HAPPYDOC_TRANSCRIBE_SERVER_URL}/transcription/start`;

    const makeRequest = async () => {
        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(postData),
            });

            if (!response.ok) {
                const responseText = await response.text();
                throw new Error(`Transcribe Audio Failed: ${response.status} - ${responseText}`);
            }

            const responseData = await response.json();

            if (!responseData.message) {
                throw new Error("No message returned");
            }

            return { error: null, data: responseData };
        } catch (error: any) {
            return { error, data: null };
        }
    };

    const { error, data } = await retryWrapper(makeRequest);

    if (error) {
        return { error, data: null };
    }

    return { error: null, data };
};

/**
 * Persists a failure status for a clip via the Transcribe service (service role), so the browser
 * does not need to update `transcript_sections` under RLS.
 */
export const persistTranscriptSectionFailureApi = async (params: {
    transcriptSectionId: string;
    patientRecordId: string;
    status: TranscriptSectionStatus;
    transcriptionId?: string | null;
    accountId?: string | null;
    unprocessedFilename?: string | null;
}): Promise<{ error: null } | { error: Error }> => {
    let statusPayload: string;
    switch (params.status) {
        case TranscriptSectionStatus.FailedIncompleteUpload:
            statusPayload = "failed_incomplete_upload";
            break;
        case TranscriptSectionStatus.FailedErrorProcessing:
            statusPayload = "failed_error_processing";
            break;
        case TranscriptSectionStatus.FailedBlankTranscript:
            statusPayload = "failed_blank_transcript";
            break;
        case TranscriptSectionStatus.FailedIrrelevantInformation:
            statusPayload = "failed_irrelevant_information";
            break;
        case TranscriptSectionStatus.FailedPartialTranscription:
            statusPayload = "failed_partial_transcription";
            break;
        default:
            Sentry.captureMessage(`Unexpected status passed to persistTranscriptSectionFailureApi: ${params.status}`, {
                level: "error",
                extra: { status: params.status, transcriptSectionId: params.transcriptSectionId },
            });
            statusPayload = "failed_error_processing";
    }
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        return { error: new Error("No effective session") };
    }
    const { accessToken } = effectiveSession;
    const url = `${VITE_HAPPYDOC_TRANSCRIBE_SERVER_URL}/transcription/section/mark-failed`;
    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                transcriptSectionId: params.transcriptSectionId,
                patientRecordId: params.patientRecordId,
                status: statusPayload,
                transcriptionId: params.transcriptionId ?? null,
                accountId: params.accountId ?? null,
                unprocessedFilename: params.unprocessedFilename ?? null,
            }),
        });
        if (!response.ok) {
            const text = await response.text();
            return { error: new Error(text || `mark-failed failed: ${response.status}`) };
        }
        return { error: null };
    } catch (e) {
        return { error: e instanceof Error ? e : new Error(String(e)) };
    }
};

export const uploadChunk = async (
    audioFile: File | Blob,
    transcriptionId: string,
    transcriptSectionId: string,
    patientRecordId: string,
    chunkIndex: number,
    end: boolean,
    docIds: string[] = [],
    transcriptSectionIds: string[] = [],
    documentAssetIds: string[] = [],
): Promise<{ error: null; data: any } | { error: any; data: null }> => {
    const effectiveSession = await getEffectiveSession();

    if (!effectiveSession) {
        Sentry.captureException(new Error("No effective session found"));
        return { error: new Error("No effective session found"), data: null };
    }

    const { userId, accessToken } = effectiveSession;

    try {
        const formData = new FormData();
        formData.append("file", audioFile);
        formData.append("userId", userId);
        formData.append("transcriptionId", transcriptionId);
        formData.append("patientRecordId", patientRecordId);
        formData.append("end", end.toString());
        formData.append("documentIds", JSON.stringify(docIds));
        formData.append("transcriptSectionIds", JSON.stringify(transcriptSectionIds));
        formData.append("documentAssetIds", JSON.stringify(documentAssetIds));

        const url = `${VITE_HAPPYDOC_TRANSCRIBE_SERVER_URL}/transcription/upload/${transcriptSectionId}/chunk/${chunkIndex}`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
        });

        if (!response.ok) {
            const responseText = await response.text();
            throw new Error(`Upload Chunk Failed: ${response.status} - ${responseText}`);
        }

        return { error: null, data: response.status };
    } catch (error: any) {
        Sentry.captureException(error);
        return { error, data: null };
    }
};

export const addTerm = async (
    session: Session,
    misspelling: string,
    term: string,
    patientId?: string,
    documentId?: string,
) => {
    try {
        // apiFetch sends the selected clinic (and impersonation target). Without the header the
        // server falls back to the caller's own account, so a member who also owns a personal
        // account filed terms against that account instead of the clinic (EN-1304).
        const res = await apiFetch<Record<string, unknown>>("correction/terms", {
            method: "POST",
            json: {
                patientId,
                documentId,
                term,
                misspelling,
                userId: session?.user?.id,
                email: session?.user?.email,
            },
        });
        return res ?? {};
    } catch (error: any) {
        Sentry.captureException(error);
        return { error: true };
    }
};

/**
 * Send an email to validate email
 * @param id doctor or communication id
 * @param email to validate email
 */
export const sendValidateEmail = async (id: string, email: string, validateType: ValidateType) => {
    try {
        const res = await apiFetch<{ error: string | null; data: any }>("email/sendValidateEmail", {
            method: "POST",
            body: JSON.stringify({
                email,
                id,
                validateType,
            }),
        });
        return res ?? { error: null, data: null };
    } catch (error: any) {
        Sentry.captureException(error);
        return { error: true };
    }
};

/**
 * Send a magic link to a doctor or account to allow them to sign in without a password
 * @param id doctor or communication id
 * @param validateType validate type
 */
export const sendMagicLink = async (id: string, validateType: ValidateType) => {
    try {
        const url = `${VITE_HAPPYDOC_API_SERVER_URL}/email/sendMagicLink`;

        const effectiveSession = await getEffectiveSession();

        if (!effectiveSession) {
            throw new Error("No effective session found");
        }

        const { accessToken } = effectiveSession;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                id,
                validateType,
            }),
        });
        return await response.json();
    } catch (error: any) {
        Sentry.captureException(error);
        return { error: true };
    }
};

/**
 * Retrieves appointments based on the specified criteria.
 *
 * @param queryStartDate - The day to fetch, as an instant. Which calendar DAY that instant falls
 *   on is decided by `zone` below, not by the Date itself — a JS `Date` carries no zone.
 * @param integration_name - Unused by the appointments route today: it never reads
 *   `integrationName`, and `authenticateIntegrations` filters on an `ids` field no caller sends,
 *   so every call resolves to all of the account's active integrations. Callers pass `[]`.
 * @param zone - Optional IANA zone (e.g. `America/Denver`) used to derive the day boundaries.
 *   Omitted, this falls back to the BROWSER's zone, which is the historical behaviour and is what
 *   the appointments-list Sync button wants: it is showing the user the day they are looking at.
 *
 *   Pass it when the caller knows the CLINIC's zone and that is the one that matters. The two can
 *   differ — a remote/RDP setup session, or a server whose clock zone was never configured — and
 *   the difference is a whole day at the edges: 6pm in Denver is already tomorrow in UTC, so a
 *   UTC-clocked machine would fetch tomorrow's schedule and show the clinic an empty today.
 *   `clientTimezoneOffsetHours` follows the same zone, which also makes the server's
 *   `clinic_time_zone` backfill read the clinic's offset rather than the device's.
 * @returns A Promise that resolves to an object containing the retrieved appointments and any errors.
 */
export const getAppointments = async (
    queryStartDate: Date,
    integration_name: Array<string>,
    zone?: string,
): Promise<{ data: PatientRecord[]; error: null } | { data: null; error: any }> => {
    try {
        const effectiveSession = await getEffectiveSession();
        if (!effectiveSession) {
            Sentry.captureException(new Error("No effective session found"));
            return { error: new Error("No effective session found"), data: null };
        }

        const { userId } = effectiveSession;
        // `{}` rather than `{ zone: undefined }` on the fallback: Luxon treats an explicit
        // `undefined` zone as "use the default" too, but passing the empty object keeps the
        // no-zone call byte-identical to what this line did before the parameter existed.
        const localDate = DateTime.fromJSDate(queryStartDate, zone ? { zone } : {});

        // Also catches an unrecognised zone string — `fromJSDate` with a bad zone yields an
        // invalid DateTime rather than throwing, and silently shipping `null` StartTime/EndTime
        // would query the wrong window instead of failing.
        if (!localDate.isValid) {
            throw new Error(`Invalid date: ${queryStartDate}${zone ? ` (zone: ${zone})` : ""}`);
        }

        const startOfDayUTC = localDate.startOf("day").toUTC().toISO();
        const endOfDayUTC = localDate.endOf("day").toUTC().toISO();
        const clientTimezoneOffsetHours = localDate.offset / 60;

        const responseData = await apiFetch<{ updatedRecords?: PatientRecord[]; errors?: any[] }>(
            "integration/appointments",
            {
                method: "POST",
                // 120s > the 90s the backend waits on the bridge for a slow Cornerstone/AVImark
                // query (see happybridge.ts's getSqlAppointments) — matching api/doctors.ts's
                // syncDoctorsFromPims 120s/90s margin exactly, not just exceeding it. A thinner
                // margin (100s was tried first) leaves too little room for the backend's own
                // pre-bridge work (auth, doctor lookups) and ordinary network/proxy latency
                // before that 90s clock even starts — the client must never give up before the
                // server does.
                timeout: 120_000,
                // ky's retry.retryOnTimeout defaults to false, so this retry config was never
                // compounding a slow-server timeout with fresh sync attempts — a timeout throws
                // immediately regardless of `limit`. Kept as-is: it still covers genuine
                // transient failures (a network blip, a brief 502/503 from a load balancer)
                // that have nothing to do with the bridge being slow.
                retry: {
                    limit: 3,
                    methods: ["POST"],
                },
                json: {
                    userId,
                    integrationName: integration_name,
                    queryParams: { StartTime: startOfDayUTC, EndTime: endOfDayUTC },
                    timezoneOffset: clientTimezoneOffsetHours,
                },
            },
        );

        if (responseData?.errors?.length) {
            throw new Error(`Server reported errors: ${JSON.stringify(responseData.errors)}`);
        }

        return { data: responseData?.updatedRecords ?? [], error: null };
    } catch (error: any) {
        return { error, data: null };
    }
};

export const exportRecordAsNote = async (
    session: Session | null,
    userId: string,
    documentId: string,
    opts: { timeoutMs?: number } = {},
): Promise<any> => {
    // Set just before the request is dispatched, so a throw before that point is "not_sent".
    let requestSent = false;
    try {
        const effectiveSession = await getEffectiveSession();
        if (!effectiveSession) {
            throw new Error("No effective session found");
        }

        requestSent = true;
        return await apiFetch("integration/export-note", {
            method: "POST",
            ...(opts.timeoutMs ? { timeout: opts.timeoutMs } : {}),
            json: {
                userId: effectiveSession.userId,
                documentId,
                integrationName: "bitwerx",
            },
        });
    } catch (error: any) {
        // ky's HTTPError carries the raw Response on error.response - read the body so
        // callers (Export.tsx) can match on error_code strings like bridge_not_connected,
        // the same way exportCornerstoneRecord already surfaces its server response body.
        let errorMessage: string | undefined;
        if (error?.response) {
            try {
                errorMessage = await error.response.text();
            } catch {
                errorMessage = undefined;
            }
        }
        // A ky HTTPError carries a response. Without one: nothing was sent ("not_sent"), or
        // the request went out but nothing came back ("no_response") - the server may still
        // be writing to the PIMS.
        return {
            error: true,
            ...(errorMessage ? { error_message: errorMessage } : {}),
            failure: error?.response ? "http" : requestSent ? "no_response" : "not_sent",
            // A gateway 502/503/504 means the write may have landed (parseExportError).
            ...(typeof error?.response?.status === "number" ? { status: error.response.status } : {}),
        };
    }
};

// _session is accepted for call-site parity with exportRecordAsNote, but intentionally
// unused — auth always flows through the effective (impersonation-aware) session below.
export const exportCornerstoneRecord = async (
    _session: Session,
    userId: string,
    documentId: string,
    staffId?: string,
): Promise<any> => {
    const controller = new AbortController();
    // API-Server's own worst case for this call is ~94s: up to 30s waiting on the bridge's
    // db.write, then — if that doesn't get a definitive answer — up to ~64s reconciling
    // directly with Cornerstone before giving up (see cornerstoneWriteback.ts's
    // reconcileCornerstoneWrite / RECONCILE_ATTEMPTS / RECONCILE_INTERVAL_MS). This client
    // timeout must stay comfortably above that, or the browser gives up and shows a failure
    // toast before the backend ever gets a chance to confirm the write actually landed — same
    // reasoning, and the same 120s value, as syncDoctorsFromPims's margin over its own 90s
    // bridge timeout, and getAppointments's margin over its own 90s bridge timeout below.
    const timeoutId = setTimeout(() => controller.abort(), 120_000);
    // requestSent/gotResponse let callers tell "never sent" and "server answered with an error"
    // from "sent but no response" (timeout/abort/offline), where the write may still be landing.
    let requestSent = false;
    let gotResponse = false;
    try {
        const effectiveSession = await getEffectiveSession();
        if (!effectiveSession) {
            throw new Error("No effective session found");
        }
        const { accessToken, userId: effectiveUserId, isImpersonating } = effectiveSession;
        const selectedClinicAccountId = getStoredSelectedClinicAccountId(effectiveUserId);

        requestSent = true;
        const response = await fetch(`${VITE_HAPPYDOC_API_SERVER_URL}/integration/export-cornerstone`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${accessToken}`,
                ...(isImpersonating ? { "X-Effective-User-Id": effectiveUserId } : {}),
                ...(selectedClinicAccountId ? { "X-HappyDoc-Account-Id": selectedClinicAccountId } : {}),
            },
            body: JSON.stringify({
                userId: userId,
                documentId: documentId,
                integrationName: "bitwerx",
                ...(staffId ? { staffId } : {}),
            }),
            signal: controller.signal,
        });

        if (!response.ok) {
            gotResponse = true;
            const status = response.status;
            let bodyText: string;
            try {
                bodyText = await response.text();
            } catch {
                bodyText = "Failed to read error response body";
            }
            throw new Error(`Export Document Failed: ${status} - ${bodyText}`);
        } else {
            // A 2xx whose body isn't JSON (or is cut off by the abort timer) leaves gotResponse false, so it is
            // "no_response". Not reported as success: a non-JSON 200 may not be from API-Server at all (a clinic
            // Wi-Fi login page or proxy answers 200 with HTML), and then nothing was written. The request_lost
            // card it maps to says "if the note is there, you're done" and offers no Try again, so it can't
            // lead to a duplicate on its own. Sentry still tells it apart: this throws a SyntaxError, a network
            // dropout a TypeError.
            return await response.json();
        }
    } catch (error: any) {
        Sentry.captureException(error);
        const message = error instanceof Error ? error.message : String(error);
        return {
            error: true,
            error_message: message || "Unknown error",
            // A rejected fetch ("Failed to fetch", abort) can't say whether the request reached API-Server:
            // the browser reports a DNS failure and a connection reset mid-request the same way. So a fetch
            // that started counts as maybe-written; a known-offline browser is caught before sending instead.
            failure: gotResponse ? "http" : requestSent ? "no_response" : "not_sent",
        };
    } finally {
        clearTimeout(timeoutId);
    }
};

export const exportDocumentToShepherd = async (
    documentId: string,
    email?: string,
    password?: string,
    pin?: string,
): Promise<{ status: number; data: any }> => {
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        return { status: 401, data: { success: false, error: "No session" } };
    }
    const { userId, accessToken } = effectiveSession;
    const selectedClinicAccountId = getStoredSelectedClinicAccountId(userId);

    const body: Record<string, string> = { documentId };
    if (email) body.email = email;
    if (password) body.password = password;
    if (pin) body.pin = pin;

    const response = await fetch(`${VITE_HAPPYDOC_API_SERVER_URL}/integration/export-shepherd`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            ...(selectedClinicAccountId ? { "X-HappyDoc-Account-Id": selectedClinicAccountId } : {}),
        },
        body: JSON.stringify(body),
    });

    let data: any;
    try {
        data = await response.json();
    } catch {
        data = { success: false, error: "Failed to parse response" };
    }
    return { status: response.status, data };
};

/** Email-only (no password) reuses the cached Shepherd session for that email instead of logging in again. */
export type ShepherdCredentials = { email: string; password?: string; pin?: string };
export type ShepherdTemplateResult<T> = { status: number; data: T | { needsCredentials?: boolean; error?: string } };

/** https always; plain http only for local development (localhost / loopback). */
const isSecureApiTransport = (): boolean => {
    try {
        const u = new URL(VITE_HAPPYDOC_API_SERVER_URL ?? "", window.location.href);
        return (
            u.protocol === "https:" ||
            (u.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(u.hostname))
        );
    } catch {
        return false;
    }
};

// Login attempt rate limiting/lockout is enforced server-side (per-user and per-email buckets shared with
// /export-shepherd, see API-Server shepherdSession.ts), so the client deliberately doesn't duplicate it.
const postShepherdTemplates = async <T>(
    path: string,
    body: Record<string, string>,
): Promise<ShepherdTemplateResult<T>> => {
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) return { status: 401, data: { error: "No session" } };
    // Never put a Shepherd password on an unencrypted connection.
    if (body.password && !isSecureApiTransport()) {
        return { status: 0, data: { error: "Refusing to send Shepherd credentials over an insecure connection." } };
    }
    const { userId, accessToken, isImpersonating } = effectiveSession;
    const selectedClinicAccountId = getStoredSelectedClinicAccountId(userId);
    let response: Response;
    try {
        response = await fetch(`${VITE_HAPPYDOC_API_SERVER_URL}/integration/${path}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                ...(isImpersonating ? { "X-Effective-User-Id": userId } : {}),
                ...(selectedClinicAccountId ? { "X-HappyDoc-Account-Id": selectedClinicAccountId } : {}),
            },
            body: JSON.stringify(body),
        });
    } catch {
        // Network failure: surface as data (status 0) so callers show an error instead of an unhandled rejection.
        return { status: 0, data: { error: "Could not reach HappyDoc. Check your connection and try again." } };
    }
    let data: any;
    try {
        data = await response.json();
    } catch {
        data = { error: "Failed to parse response" };
    }
    return { status: response.status, data };
};

// A PIN is part of Shepherd's login step, so it is only ever sent together with a password, never alone.
const credsBody = (creds?: ShepherdCredentials): Record<string, string> =>
    creds
        ? {
              email: creds.email,
              ...(creds.password ? { password: creds.password } : {}),
              ...(creds.password && creds.pin ? { pin: creds.pin } : {}),
          }
        : {};

export const listShepherdTemplates = (creds?: ShepherdCredentials) =>
    postShepherdTemplates<{ templates: Array<{ id: string; name: string }> }>(
        "shepherd/physical-exam-templates",
        credsBody(creds),
    );

export const getShepherdTemplateFields = (templateId: string, creds?: ShepherdCredentials) =>
    postShepherdTemplates<{
        id: string;
        name: string;
        fields: Array<{ name: string; order: number; hasTextInput: boolean }>;
    }>("shepherd/physical-exam-templates/fields", { templateId, ...credsBody(creds) });

export const getIntegrationResources = async (): Promise<any> => {
    try {
        const effectiveSession = await getEffectiveSession();
        if (!effectiveSession) {
            Sentry.captureException(new Error("No effective session found"));
            return { error: true, error_message: "No effective session found" };
        }

        return await apiFetch("integration/account-resources", {
            method: "POST",
            json: { userId: effectiveSession.userId },
        });
    } catch (error: any) {
        return { error: true, error_message: error.message || "Unknown error" };
    }
};

export const newSignUpSlackNotification = async (email?: string, id?: string) => {
    try {
        if (!email || !id) {
            Sentry.captureMessage("Missing required parameters - newSignUpSlackNotification", {
                extra: { email, id },
                level: "error",
            });
            return { error: true };
        }

        const url = `${VITE_HAPPYDOC_API_SERVER_URL}/public/new-signup`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                email,
                id,
            }),
        });

        return response;
    } catch (error: any) {
        Sentry.captureException(error);
        return { error: true };
    }
};

export const doctorsModifiedSlackNotification = async (
    session: Session | null,
    clinicName: string,
    doctorName: string,
    doctorId: string,
    doctorEmail: string,
    doctorAdded: boolean,
) => {
    try {
        if (
            !session?.user?.id ||
            !session?.access_token ||
            !doctorName ||
            !doctorId ||
            !doctorEmail ||
            doctorAdded === undefined
        ) {
            Sentry.captureMessage("Missing required parameters - doctorsModifiedSlackNotification", {
                extra: { session, clinicName, doctorName, doctorId, doctorEmail, doctorAdded },
                level: "error",
            });
            return { error: true };
        }

        const url = `${VITE_HAPPYDOC_API_SERVER_URL}/slack/doctors-modified`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
                accountId: session.user.id,
                clinicName,
                doctorName,
                doctorId,
                doctorEmail,
                doctorAdded,
            }),
        });

        return response;
    } catch (error: any) {
        Sentry.captureException(error);
        return { error: true };
    }
};

export const postNewFeedbackToSlack = async (
    chat_completion_id?: string,
    feedbackType?: string,
    feedback?: string,
    doc_name?: string,
) => {
    try {
        if (!chat_completion_id || !feedbackType || !feedback) {
            Sentry.captureMessage("Missing required parameters - postNewFeedbackToSlack", {
                extra: { chat_completion_id, feedbackType, feedback, doc_name },
                level: "error",
            });
            return { error: true };
        }

        const effectiveSession = await getEffectiveSession();

        if (!effectiveSession) {
            Sentry.captureException(new Error("No effective session found"));
            return { error: true };
        }

        const { session, userId, accessToken } = effectiveSession;

        const url = `${VITE_HAPPYDOC_API_SERVER_URL}/slack/provided-feedback`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                chat_completion_id,
                feedbackType,
                feedback,
                acct_id: userId,
                doc_name,
                email: session.user.email,
            }),
        });
        return response;
    } catch (error: any) {
        Sentry.captureException(error);
        return { error: true };
    }
};

/**
 * Send a slack notification for a new custom field upsert
 * @param session - The current session
 * @param email - The email of the user
 * @param id - The id of the user
 * @param field - The field that was upserted
 * @returns { error: true } if the request returns an error
 */
export const upsertedCustomFieldSlack = async (
    session: Session,
    email?: string,
    id?: string,
    isUpdated?: boolean,
    field?: CreatorPromptField,
): Promise<{ error: boolean }> => {
    try {
        if (!email || !id || !field) {
            Sentry.captureMessage("Missing required parameters - upsertedCustomFieldSlack", {
                extra: { email, id, field },
                level: "error",
            });
            return { error: true };
        }

        const url = `${VITE_HAPPYDOC_API_SERVER_URL}/slack/custom-field-upsert`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
                accountId: id,
                email,
                field,
                isUpdated,
            }),
        });

        if (!response.ok) {
            throw new Error(`Failed to send slack notification - upsertedCustomFieldSlack: ${response.statusText}`);
        }

        return { error: false };
    } catch (error: any) {
        Sentry.captureException(error);
        return { error: true };
    }
};

export const invalidateDocumentCache = async (documentId: string) => {
    try {
        if (!documentId) {
            Sentry.captureMessage("Missing required parameters documentId - invalidateDocumentCache");
            return { error: true };
        }

        await apiFetch("generate/invalidateCache", {
            method: "POST",
            json: { key: documentId },
        });
    } catch {
        return { error: true };
    }
};

export const addPatient = async (params: AddPatientRequest): Promise<{ error: any; data: PatientRecord | null }> => {
    try {
        const effectiveSession = await getEffectiveSession();
        if (!effectiveSession) {
            Sentry.captureException(new Error("No effective session found"));
            return { error: new Error("No effective session found"), data: null };
        }

        params.userId = effectiveSession.userId;

        const data = await apiFetch<PatientRecord>("db/addPatient", {
            method: "POST",
            retry: {
                limit: 3,
                methods: ["POST"],
            },
            json: params,
        });
        return { error: null, data: data ?? null };
    } catch (error: any) {
        return { error, data: null };
    }
};

export const search = async (query: string): Promise<{ error: any; data: PatientRecord[] | null }> => {
    try {
        const effectiveSession = await getEffectiveSession();
        if (!effectiveSession) {
            Sentry.captureException(new Error("No effective session found"));
            return { error: new Error("No effective session found"), data: null };
        }

        const data = await apiFetch<PatientRecord[]>("search/searchPatients", {
            method: "POST",
            json: {
                userId: effectiveSession.userId,
                query,
            },
        });
        return { error: null, data: data ?? [] };
    } catch (error) {
        return { error, data: null };
    }
};

export const syncCollections = async (): Promise<{ error: any; data: any[] | null }> => {
    try {
        const effectiveSession = await getEffectiveSession();
        if (!effectiveSession) {
            Sentry.captureException(new Error("No effective session found"));
            return { error: new Error("No effective session found"), data: null };
        }

        const data = await apiFetch<any[]>("search/syncCollections", {
            method: "POST",
            json: { userId: effectiveSession.userId },
        });
        return { error: null, data: data ?? null };
    } catch (error) {
        return { error, data: null };
    }
};

/**
 * Create a custom document type from a PDF file.
 *
 * @param customPdfFilename - The filename of the PDF file to create a custom document type from.
 * @returns A promise that resolves to the new document type data.
 */
export const createCustomDocumentTypeFromPdf = async (customPdfFilename: string): Promise<DocumentType> => {
    return expectJsonBody(
        await apiFetch<DocumentType>("custom-pdf", {
            method: "POST",
            timeout: 300_000, // 5 minutes
            retry: {
                limit: 3, // Docling can eat memory and kill its container, so we need to retry
                methods: ["POST"],
                delay: (attemptCount) => 5 * 3 ** (attemptCount - 1) * 1000, // 5s, 15s, 45s delay giving container time to restart
            },
            json: { customPdfFilename },
        }),
        "POST custom-pdf",
    );
};

/**
 * Creates an export document of the specified type.
 *
 * This function sends a POST request to the HappyDoc API server to create an export document.
 * It retrieves the current session from Supabase authentication and includes the session token
 * in the request headers for authorization.
 *
 * @param documentTypeId - The ID of the document type to be exported.
 * @param templateKey - The template key for the export document.
 * @param font - The font to be used in the export document.
 * @param noteType - The type of note to be exported (Note or SOAP).
 * @param ezyvetSectionMap - ezyVet record class per section; null resets to match-by-name, undefined leaves it unchanged.
 * @returns A promise that resolves to an object containing the document export id and path.
 * @throws Logs with Sentry and rethrows errors.
 */
export const createExportDocument = async (
    documentTypeId: string,
    templateKey: string,
    font: string | null,
    noteType: string,
    alertColor: string | null,
    alertDisabled: boolean,
    shepherdTemplate?: { id: string | null; name: string | null; fieldMap: ShepherdTemplateFieldMapEntry[] },
    ezyvetSectionMap?: EzyvetSectionMapEntry[] | null,
): Promise<{ documentExportId: string; path: string }> => {
    const response = await apiFetch<{ documentExportId: string; path: string }>("integration/export/create", {
        method: "POST",
        json: {
            documentTemplateKey: templateKey,
            documentTypeId: documentTypeId,
            fontFamily: font,
            noteType,
            cornerstoneAlertColor: alertColor,
            cornerstoneAlertDisabled: alertDisabled,
            ...(shepherdTemplate
                ? {
                      shepherdTemplateId: shepherdTemplate.id,
                      shepherdTemplateName: shepherdTemplate.name,
                      shepherdTemplateFieldMap: shepherdTemplate.fieldMap,
                  }
                : {}),
            // null clears the stored map (match sections by name); omitted leaves it untouched.
            ...(ezyvetSectionMap !== undefined ? { ezyvetSectionMap } : {}),
        },
    });

    return expectJsonBody(response, "POST integration/export/create");
};

/**
 * Fetches export document template keys from the HappyDoc API server.
 *
 * @returns {Promise<{ error: any, data: any }>} An object containing either the fetched data or an error.
 *
 * @throws {Error} Throws an error if the response status is greater than 299 or if there is an issue with the fetch request.
 */
export const getExportDocumentTemplates = async (): Promise<{ error: any | null; data: string[] | null }> => {
    try {
        const data = await apiFetch<string[]>("integration/export/create");
        return { error: null, data: data ?? null };
    } catch (error: any) {
        return { error, data: null };
    }
};

/**
 * Changes the PIMS (Property Information Management System) by making a POST request to the specified API endpoint.
 *
 * @param {PIMSType} newPIMS - The new PIMS data to be sent in the request body.
 * @returns {Promise<{ error: any | null, data: any | null }>} - A promise that resolves to an object containing either the error or the data from the response.
 *
 * @throws Will throw an error if the session retrieval fails or if the fetch request fails.
 */
export const changePIMS = async (newPIMS: PIMSType): Promise<{ error: any | null; data: any | null }> => {
    try {
        const effectiveSession = await getEffectiveSession();
        if (!effectiveSession) {
            Sentry.captureException(new Error("No effective session found"));
            return { error: new Error("No effective session found"), data: null };
        }

        const data = await apiFetch<any>("db/changePIMS", {
            method: "POST",
            json: { newPIMS, accountId: effectiveSession.userId },
        });
        return { error: null, data: data ?? null };
    } catch (error: any) {
        let errorMessage = "Failed to change PIMS";
        try {
            const responseBody = await error?.response?.json();
            errorMessage = responseBody?.error || errorMessage;
        } catch {
            errorMessage = error?.message || errorMessage;
        }
        return { error: errorMessage, data: null };
    }
};

export const importAppointmentsFile = async (
    payload: ImportFilePayload,
    processRecords: boolean,
): Promise<{ data: ImportFileResponse | null; error: null } | { data: null; error: any }> => {
    try {
        const data = await apiFetch<ImportFileResponse>(
            `v2/integration/import-file?process_records=${processRecords}`,
            { method: "POST", json: payload },
        );
        return { error: null, data: data ?? null };
    } catch (error: any) {
        let errorMessage = "Failed to import appointments";
        try {
            const responseBody = await error?.response?.json();
            errorMessage = responseBody?.error || errorMessage;
        } catch {
            errorMessage = error?.message || errorMessage;
        }
        return { error: errorMessage, data: null };
    }
};

export const requestInsightsAccess = async () => {
    try {
        await apiFetch("db/requestInsightsAccess", { method: "POST" });
        return { error: null };
    } catch (error: any) {
        return { error };
    }
};

/**
 * EN-917: this rethrows after reporting to Sentry. Returning [] instead made a
 * failed fetch indistinguishable from "no release notes exist" — the caller's
 * catch never fired, so its error toast was dead code and the user saw a
 * silently empty page.
 */
export const getReleaseNotes = async (page: number, limit: number): Promise<ReleaseNotes[]> => {
    try {
        const path = `db/releaseNotes?page=${page}&limit=${limit}`;

        const response = await apiFetch<ReleaseNotes[]>(path);

        return response ?? [];
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
};

export const getLatestReleaseNotes = async (): Promise<ReleaseNotes | null> => {
    const makeRequest = async () => {
        try {
            const response = await apiFetch<ReleaseNotes>("db/releaseNotes/latest");

            return { data: response ?? null, error: null };
        } catch (error: any) {
            return { data: null, error };
        }
    };

    const { data, error } = await makeRequest();
    return error ? null : data;
};

/** Upload rejection codes the web app explains to the vet instead of offering a plain retry. */
export const PDF_PASSWORD_PROTECTED_CODE = "PDF_PASSWORD_PROTECTED";

/** A failed upload. `code` and `userMessage` are set when the server said why (e.g. {@link PDF_PASSWORD_PROTECTED_CODE}). */
export class DocumentAssetUploadError extends Error {
    constructor(
        message: string,
        readonly code?: string,
        readonly userMessage?: string,
    ) {
        super(message);
        this.name = "DocumentAssetUploadError";
        // keeps `instanceof` working when this compiles to ES5
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

// API-Server's upload filter and size cap answer with these codes (multerHttpErrors.ts). Its byte check
// (documentAssetMime.ts) answers with a message and no code, so that one is matched by its opening words.
const INVALID_FILE_TYPE_CODE = "INVALID_FILE_TYPE";
const FILE_TOO_LARGE_CODE = "LIMIT_FILE_SIZE";
const UNVERIFIED_FILE_TYPE_MESSAGE_START = "File type is not allowed";

/** The reason shown on a failed upload row, for rejections the vet can fix by choosing another file. */
export function uploadErrorMessageFor(error: Error): string | undefined {
    if (!(error instanceof DocumentAssetUploadError)) return undefined;
    if (error.code === PDF_PASSWORD_PROTECTED_CODE) return error.userMessage;
    if (error.code === INVALID_FILE_TYPE_CODE || error.userMessage?.startsWith(UNVERIFIED_FILE_TYPE_MESSAGE_START)) {
        return "Only PDF, JPG and PNG files can be uploaded.";
    }
    if (error.code === FILE_TOO_LARGE_CODE) return "This file is larger than 25 MB.";
    return undefined;
}

export const UploadDocumentAsset = async (
    patientRecordId: string,
    file: Blob,
): Promise<{ data: DocumentAsset | null; err: Error | null }> => {
    const effectiveSession = await getEffectiveSession();

    if (!effectiveSession) {
        Sentry.captureException(new Error("No effective session found"));
        return { data: null, err: new Error("No effective session found") };
    }

    const { userId, accessToken, isImpersonating } = effectiveSession;
    const selectedClinicAccountId = getStoredSelectedClinicAccountId(userId);

    try {
        const url = `${VITE_HAPPYDOC_API_SERVER_URL}/db/documentAsset`;

        const formData = new FormData();
        formData.append("files", file);
        formData.append("accountId", userId);
        formData.append("patientRecordId", patientRecordId);

        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                ...(isImpersonating ? { "X-Effective-User-Id": userId } : {}),
                ...(selectedClinicAccountId ? { "X-HappyDoc-Account-Id": selectedClinicAccountId } : {}),
            },
            body: formData,
        });

        if (!response.ok) {
            const responseText = await response.text();
            let body: { code?: string; message?: string } | undefined;
            try {
                body = JSON.parse(responseText);
            } catch {
                // not JSON (e.g. a proxy error page); keep the raw text in the message below
            }
            throw new DocumentAssetUploadError(`Failed to upload file: ${responseText}`, body?.code, body?.message);
        }

        const resp = await response.json();
        const data = resp?.assets[0];

        return { data, err: null };
    } catch (error: any) {
        return { data: null, err: error };
    }
};

/**
 * Fetch a chart document's filled PDF.
 * Uses the effective session (impersonation-aware) for auth.
 * Throws if no session or the request fails.
 *
 * @param chatCompletionId - ID of the chart chat completion to render as a PDF
 * @param editable - If true, keeps form fields editable; if false, flattens the PDF
 * @returns A Blob containing the PDF bytes
 */
export const getChartPdf = async (chatCompletionId: string, editable: boolean = false): Promise<Blob> => {
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        throw new Error("No effective session found");
    }

    const { userId, accessToken, isImpersonating } = effectiveSession;
    const selectedClinicAccountId = getStoredSelectedClinicAccountId(userId);

    try {
        const response = await api.post(`document/pdf${editable ? "?editable=1" : ""}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                ...(isImpersonating ? { "X-Effective-User-Id": userId } : {}),
                ...(selectedClinicAccountId ? { "X-HappyDoc-Account-Id": selectedClinicAccountId } : {}),
            },
            json: { chatCompletionId },
        });
        return await response.blob();
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: "api_fetch_error",
                endpoint: "v1/document/pdf",
            },
        });
        throw error;
    }
};

/**
 * Send a document (chat completion) via email.
 * @param chatCompletionId - The ID of the chat completion to send
 * @param subject - The email subject
 * @param toEmail - The recipient email address
 * @param replyTo - The reply-to email address
 * @param ccEmails - Array of CC email addresses (optional)
 * @returns Promise with the response data or error
 */
export const sendDocumentEmail = async (
    chatCompletionId: string,
    subject: string,
    toEmails: string[],
    replyTo: string,
    ccEmails?: string[],
): Promise<{ error: any | null; data: any | null }> => {
    try {
        const data = await apiFetch<any>("email/sendDocumentEmail", {
            method: "POST",
            json: { chatCompletionId, subject, toEmails, ccEmails, replyTo },
        });
        return { error: null, data: data ?? null };
    } catch (error: any) {
        return { error, data: null };
    }
};
